import { useMemo }                           from "react";
import { Grid, Icon, Typography }            from "@gupshup-frontend/gupshup-ts-components";

import Tooltip                               from "./Tooltip";
import {
  SEE_ALL_OPTIONS,
  MAX_BUTTON_INDEX,
  BUTTON_LIST_TYPES,
  DEFAULT_BUTTON_IMAGES,
  TEMPLATE_COPY_CODE_TEXT,
  TEMPLATE_PREVIEW_DATA_TESTID,
  // Vendored from the old aliased containers/Whatsapp/CreateTemplate/config module
  // (see constant.js).
  AUTOFILL_LABEL,
  COPY_CODE_LABEL,
  PIX_CODE_TEXT,
  // i18n-library replacement (see constant.js) — used below in place of the old
  // t(btn.text) call.
  BUTTON_TEXT_TRANSLATIONS,
}                                            from "./constant";
import styles                                from "./style.module.css";

const ButtonPanel = (props) => {

  const {
    buttons        = [],
    showAll        = false,
    isInPreview    = false,
    isOrderDetails = false,
  } = props;

  const getButtonIcons = (buttonType, buttonText) => {
    if(buttonType === BUTTON_LIST_TYPES.PAY_NOW) {
      return "";
    }
    const iconType = showAll ? "icon" : "base_icon";
    const buttonList = Object.keys(DEFAULT_BUTTON_IMAGES);

    if (buttonType === BUTTON_LIST_TYPES.ORDER_DETAILS && buttonText === PIX_CODE_TEXT) {
      return DEFAULT_BUTTON_IMAGES[BUTTON_LIST_TYPES.COPY_CODE][iconType];
    }

    if(buttonList.includes(buttonType)) {
      return DEFAULT_BUTTON_IMAGES[buttonType][iconType];
    }
    return "";
  };

  const getAutoFillData = (url) => {
    try {
      const params = new URLSearchParams(new URL(url).search);
      return {
        otpType        : params.get("otp_type") ?? "",
        packageName    : params.get("package_name") ?? "",
        signatureHash  : params.get("signature_hash") ?? "",
        ctaDisplayName : params.get("cta_display_name") ?? "",
      };
    }
    catch (err) {
      return {};
    }
  };

  const buttonList = useMemo (() => {

    const btnList = [];

    buttons.forEach((btn, index) => {
      if (!showAll && index > (MAX_BUTTON_INDEX - 1)) return false;

      const showAllOptions = !showAll && index === (MAX_BUTTON_INDEX - 1) && buttons.length > MAX_BUTTON_INDEX;

      let buttonType = btn.type;
      let buttonValue = btn.type === BUTTON_LIST_TYPES.PHONE_NUMBER ? btn.phone_number : null;
      let buttonText = btn.type === BUTTON_LIST_TYPES.COPY_CODE && !btn.text ? TEMPLATE_COPY_CODE_TEXT : btn.text;

      if(btn.type === BUTTON_LIST_TYPES.URL && btn.url) {
        const _authData = getAutoFillData(btn.url);
        if(_authData.otpType === BUTTON_LIST_TYPES.ZERO_TAP) {
          return;
        }
        if(_authData.otpType === BUTTON_LIST_TYPES.COPY_CODE || _authData.otpType === BUTTON_LIST_TYPES.ONE_TAP) {
          buttonType = BUTTON_LIST_TYPES.OTP;
        }
        if(_authData.otpType === BUTTON_LIST_TYPES.ONE_TAP) {
          buttonText = _authData.ctaDisplayName;
        }
      }

      if (btn.type === BUTTON_LIST_TYPES.OTP && btn.otp_type === BUTTON_LIST_TYPES.ONE_TAP) {
        // btn.autofill_text is the user-edited Autofill button text (payload.js's
        // buildOtpButton) — fall back to the fixed label only when it's blank.
        buttonText = btn.autofill_text || AUTOFILL_LABEL;
        buttonType = BUTTON_LIST_TYPES.ONE_TAP;
      }

      if (btn.type === BUTTON_LIST_TYPES.OTP && btn.otp_type === BUTTON_LIST_TYPES.COPY_CODE) {
        // Same as above, for the Copy Code button's own user-edited text.
        buttonText = btn.text || COPY_CODE_LABEL;
      }

      if (btn.type === BUTTON_LIST_TYPES.URL) {
        buttonValue = btn.example ? btn.example[0] : btn.url;
      }

      if (showAllOptions) {
        buttonValue = "";
        buttonText  = SEE_ALL_OPTIONS;
        buttonType  = BUTTON_LIST_TYPES.ALLOPTIONS;
      }

      btnList.push({
        type  : buttonType,
        text  : buttonText ?? "",
        value : buttonValue ?? "",
        icon  : getButtonIcons(buttonType, buttonText),
      });
    });

    return btnList;
  }, [buttons, showAll, getButtonIcons]);

  if(buttons && buttons.length === 0) {
    return null;
  }

  return (
    <Grid marginTop = {0.5} className = {isOrderDetails && styles.orderDetailsButton || ""}>
      {buttonList?.map((btn, index) => (
        <Grid
          key         = {index}
          flexWrap    = "nowrap"
          data-testid = {TEMPLATE_PREVIEW_DATA_TESTID.BUTTON}
          className   = {`${styles.listedButtons} ${!showAll && styles.actionButtons}`}
        >
          <Tooltip placement = "top" title = {!showAll && btn.value || ""}>
            <Grid
              gap            = {0.4}
              display        = "flex"
              alignItems     = "center"
              justifyContent = {showAll? "flex-start" : "center"}
            >
              {btn.icon &&
                <Icon
                  showCursor
                  size       = {isInPreview ? 16 : 14}
                  alt        = {btn.text}
                  className  = "chip-icon"
                  customPath = {btn.icon}
                />
              }
              <Typography variant = {isInPreview ? "body2" : "caption"} fontWeight = {500} noWrap>
                {BUTTON_TEXT_TRANSLATIONS[btn.text] || btn.text}
              </Typography>
              {btn.type === BUTTON_LIST_TYPES.CPR &&
                <Icon
                  showCursor
                  size      = {isInPreview ? 16 : 14}
                  alt       = {btn.text}
                  name      = "caretDown"
                  className = "chip-icon"
                />
              }
            </Grid>
          </Tooltip>
        </Grid>
      ))}
    </Grid>
  );
};

export default ButtonPanel;
